/**
 * Campos de formulário ligados ao react-hook-form (via FormProvider/useFormContext)
 * e vestidos com MUI temado. Usados por FormDialog e telas de cadastro.
 */
import { useState, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs, { type Dayjs } from 'dayjs'
import { SearchableSelect, type SelectOption, type SelectOptionsLoader } from './SearchableSelect'
import type { SelectControlSize } from './selectTokens'
import { useDeclarativeText } from '../i18n/declarativeText'

interface BaseProps {
  name: string
  label: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  placeholder?: string
  helperText?: string
}

export type Option = SelectOption

/** Campo de texto (ou number/date/textarea via props). */
export function RHFText({
  name,
  label,
  required,
  disabled,
  placeholder,
  helperText,
  type = 'text',
  multiline,
  rows,
}: BaseProps & { type?: 'text' | 'number' | 'email'; multiline?: boolean; rows?: number }) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          label={tt(label)}
          required={required}
          disabled={disabled}
          placeholder={placeholder ? tt(placeholder) : undefined}
          type={type}
          multiline={multiline}
          rows={rows}
          fullWidth
          size="small"
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ? tt(fieldState.error.message) : helperText ? tt(helperText) : undefined}
        />
      )}
    />
  )
}

/** Select (Autocomplete single) com busca interna e lista rolável. Guarda o
 *  `value` (string) da opção; abre com 1 clique e filtra ao digitar. */
export function RHFSelect({
  name,
  label,
  required,
  disabled,
  helperText,
  options,
  loadOptions,
  selectedOptions,
  asyncKey,
  loading,
  sizeVariant,
  placeholder,
  readOnly,
}: BaseProps & {
  options?: Option[]
  loadOptions?: SelectOptionsLoader
  selectedOptions?: Option[]
  asyncKey?: string
  loading?: boolean
  sizeVariant?: SelectControlSize
}) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  const localizedOptions = options?.map((option) => ({
    ...option,
    label: tt(option.label),
    hint: option.hint ? tt(option.hint) : option.hint,
    sublabel: option.sublabel ? tt(option.sublabel) : option.sublabel,
  }))
  const localizedSelectedOptions = selectedOptions?.map((option) => ({
    ...option,
    label: tt(option.label),
    hint: option.hint ? tt(option.hint) : option.hint,
    sublabel: option.sublabel ? tt(option.sublabel) : option.sublabel,
  }))
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <SearchableSelect
          name={name}
          label={tt(label)}
          value={(field.value as string) ?? ''}
          onChange={(next) => field.onChange(next as string)}
          onBlur={field.onBlur}
          options={localizedOptions}
          loadOptions={loadOptions}
          selectedOptions={localizedSelectedOptions}
          asyncKey={asyncKey}
          loading={loading}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          clearable={!required}
          sizeVariant={sizeVariant}
          searchPlaceholder={placeholder ? tt(placeholder) : undefined}
          error={fieldState.error?.message ? tt(fieldState.error.message) : false}
          helperText={helperText ? tt(helperText) : undefined}
        />
      )}
    />
  )
}

/** Multi-seleção (Autocomplete com busca) ligada ao RHF — guarda `string[]` de values. */
export function RHFMultiAutocomplete({
  name,
  label,
  disabled,
  helperText,
  options,
  loading,
  placeholder,
  loadOptions,
  selectedOptions,
  asyncKey,
  readOnly,
}: BaseProps & {
  options?: Option[]
  loading?: boolean
  loadOptions?: SelectOptionsLoader
  selectedOptions?: Option[]
  asyncKey?: string
}) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  const localizedOptions = options?.map((option) => ({
    ...option,
    label: tt(option.label),
    hint: option.hint ? tt(option.hint) : option.hint,
    sublabel: option.sublabel ? tt(option.sublabel) : option.sublabel,
  }))
  const localizedSelectedOptions = selectedOptions?.map((option) => ({
    ...option,
    label: tt(option.label),
    hint: option.hint ? tt(option.hint) : option.hint,
    sublabel: option.sublabel ? tt(option.sublabel) : option.sublabel,
  }))
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <SearchableSelect
          name={name}
          label={tt(label)}
          value={(field.value as string[]) ?? []}
          onChange={(next) => field.onChange(next as string[])}
          onBlur={field.onBlur}
          multiple
          options={localizedOptions}
          loadOptions={loadOptions}
          selectedOptions={localizedSelectedOptions}
          asyncKey={asyncKey}
          loading={loading}
          disabled={disabled}
          readOnly={readOnly}
          searchPlaceholder={placeholder ? tt(placeholder) : undefined}
          error={fieldState.error?.message ? tt(fieldState.error.message) : false}
          helperText={helperText ? tt(helperText) : undefined}
        />
      )}
    />
  )
}

/** Toggle booleano. */
export function RHFSwitch({ name, label, disabled }: BaseProps) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label={tt(label)}
        />
      )}
    />
  )
}

// ── Date/time pickers (@mui/x-date-pickers) — 100% MUI, sem popup nativo ──────
// Contrato de dados = string: data 'YYYY-MM-DD', hora 'HH:mm' (formatos da API).
const dateFromStr = (v?: string): Dayjs | null => (v ? dayjs(v) : null)
const dateToStr = (d: Dayjs | null) => (d && d.isValid() ? d.format('YYYY-MM-DD') : '')
const timeFromStr = (v?: string): Dayjs | null => (v ? dayjs(`2000-01-01T${v}`) : null)
const timeToStr = (d: Dayjs | null) => (d && d.isValid() ? d.format('HH:mm') : '')
const dateTimeFromStr = (v?: string): Dayjs | null => (v ? dayjs(v) : null)
const dateTimeToStr = (d: Dayjs | null) => (d && d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '')

/** Data (YYYY-MM-DD) ligada ao react-hook-form. */
export function RHFDate({ name, label, required, disabled, helperText }: BaseProps) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <DatePicker
          label={tt(label)}
          disabled={disabled}
          value={dateFromStr(field.value as string)}
          onChange={(d) => field.onChange(dateToStr(d))}
          slotProps={{
            textField: {
              required,
              fullWidth: true,
              size: 'small',
              onBlur: field.onBlur,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ? tt(fieldState.error.message) : helperText ? tt(helperText) : undefined,
            },
          }}
        />
      )}
    />
  )
}

/** Hora (HH:mm, 24h) ligada ao react-hook-form. */
export function RHFTime({ name, label, required, disabled, helperText }: BaseProps) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TimePicker
          label={tt(label)}
          ampm={false}
          disabled={disabled}
          value={timeFromStr(field.value as string)}
          onChange={(d) => field.onChange(timeToStr(d))}
          slotProps={{
            textField: {
              required,
              fullWidth: true,
              size: 'small',
              onBlur: field.onBlur,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ? tt(fieldState.error.message) : helperText ? tt(helperText) : undefined,
            },
          }}
        />
      )}
    />
  )
}

interface PickerFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
  size?: 'small' | 'medium'
  fullWidth?: boolean
}

/** Data (YYYY-MM-DD) controlada por string, fora do react-hook-form (filtros, dialogs simples).
 *
 *  `min`/`max` também em YYYY-MM-DD, quando a faixa é regra e não preferência: no
 *  Painel de Jornada não existe consultar amanhã, e desabilitar no calendário evita
 *  o caminho de escolher, enviar e receber recusa do servidor. */
export function DateField({ label, value, onChange, required, disabled, size = 'small', fullWidth, min, max }: PickerFieldProps & {
  min?: string
  max?: string
}) {
  return (
    <DatePicker
      label={label}
      disabled={disabled}
      value={dateFromStr(value)}
      minDate={dateFromStr(min) ?? undefined}
      maxDate={dateFromStr(max) ?? undefined}
      onChange={(d) => onChange(dateToStr(d))}
      slotProps={{ textField: { required, size, fullWidth } }}
    />
  )
}

/** Hora (HH:mm, 24h) controlada por string, fora do react-hook-form. */
export function TimeField({ label, value, onChange, required, disabled, size = 'small', fullWidth }: PickerFieldProps) {
  return (
    <TimePicker
      label={label}
      ampm={false}
      disabled={disabled}
      value={timeFromStr(value)}
      onChange={(d) => onChange(timeToStr(d))}
      slotProps={{ textField: { required, size, fullWidth } }}
    />
  )
}

/** Data e hora (YYYY-MM-DDTHH:mm, 24h) controlada por string, fora do react-hook-form.
 *  Calendário MUI X com relógio, para filtros de período como o da Auditoria. */
export function DateTimeField({ label, value, onChange, required, disabled, size = 'small', fullWidth }: PickerFieldProps) {
  return (
    <DateTimePicker
      label={label}
      ampm={false}
      disabled={disabled}
      value={dateTimeFromStr(value)}
      onChange={(d) => onChange(dateTimeToStr(d))}
      slotProps={{ textField: { required, size, fullWidth } }}
    />
  )
}

/** Editor de data para CÉLULA de grade (DataGrid): mesmo calendário MUI X dos filtros,
 *  contrato de dados = string 'YYYY-MM-DD'. Abre o calendário ao montar; escolher uma
 *  data comita (e fecha a edição); fechar/Esc sem escolher cancela. */
export function InlineDatePicker({ initial, onCommit, onCancel }: {
  initial: string
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const [val, setVal] = useState<Dayjs | null>(() => dateFromStr(initial))
  // Abre o calendário ao montar (controlado, como o Autocomplete do select).
  const [open, setOpen] = useState(true)
  // Evita que o onClose (disparado logo após aceitar) cancele uma seleção já comitada.
  const done = useRef(false)
  return (
    <DatePicker
      open={open}
      onOpen={() => setOpen(true)}
      value={val}
      onChange={(d) => setVal(d)}
      onAccept={(d) => { done.current = true; onCommit(dateToStr(d)) }}
      onClose={() => { setOpen(false); if (!done.current) onCancel() }}
      slotProps={{ textField: { variant: 'standard', size: 'small', fullWidth: true, autoFocus: true } }}
    />
  )
}

/** Formata reais para exibição no input (sem símbolo — o "R$" vai no adorno). */
function moneyDisplay(v: number | null): string {
  if (v == null) return ''
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Campo monetário BRL controlado, sem lib de máscara: "push de dígitos" (estilo
 *  app de banco). Digitar empurra centavos pela direita (1 → 0,01; 1250 → 12,50),
 *  Backspace remove o último dígito, colar filtra não-dígitos e o caret fica
 *  sempre no fim. O valor externo é NÚMERO em reais (ou null p/ vazio); o corpo
 *  da API recebe esse número com 2 casas. */
export function MoneyField({ label, value, onChange, required, disabled, autoFocus, error, helperText, size = 'small', fullWidth = true }: {
  label: string
  value: number | null
  onChange: (v: number | null) => void
  required?: boolean
  disabled?: boolean
  autoFocus?: boolean
  error?: boolean
  helperText?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
}) {
  const tt = useDeclarativeText()
  return (
    <TextField
      label={tt(label)}
      value={moneyDisplay(value)}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 12)
        onChange(digits ? Number(digits) / 100 : null)
      }}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      error={error}
      helperText={helperText ? tt(helperText) : undefined}
      fullWidth={fullWidth}
      size={size}
      slotProps={{
        input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> },
        htmlInput: { inputMode: 'numeric' },
      }}
    />
  )
}

/** Campo monetário BRL ligado ao react-hook-form (valor number | null). */
export function RHFMoney({ name, label, required, disabled, helperText }: BaseProps) {
  const { control } = useFormContext()
  const tt = useDeclarativeText()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <MoneyField
          label={label}
          value={(field.value as number | null) ?? null}
          onChange={field.onChange}
          required={required}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ? tt(fieldState.error.message) : helperText}
        />
      )}
    />
  )
}
